import React, { useState, useEffect } from 'react';
import { Coffee, Play, Pause, Square, Clock, Maximize2, Minimize2, ChevronUp, Zap } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLocalTimer } from '../../hooks/useLocalTimer';
import { useDbUser } from '../../hooks/useDbUser';
import { db } from '../../lib/db';
import { toast } from 'sonner';
import TimeEntryModal from '../tasks/TimeEntryModal';

export function FloatingTimer() {
  const location = useLocation();
  const navigate = useNavigate();
  const { dbUserId } = useDbUser();
  const [isMinimized, setIsMinimized] = useState(true);
  const [notes, setNotes] = useState('');
  const [showTimeEntryModal, setShowTimeEntryModal] = useState(false);
  const [taskData, setTaskData] = useState<any>(null);
  
  const {
    isRunning,
    isOnBreak,
    currentTaskId,
    currentTaskTitle,
    displayTime,
    togglePause,
    startBreak,
    endBreak,
    stopTimer
  } = useLocalTimer();

  // Load task data when timer starts
  useEffect(() => {
    if (currentTaskId && dbUserId && !taskData) {
      loadTaskData();
    }
  }, [currentTaskId, dbUserId]);

  const loadTaskData = async () => {
    if (!currentTaskId || !dbUserId) return;
    
    try {
      const task = await db.tasks.findById(currentTaskId, dbUserId);
      if (task) {
        setTaskData(task);
      }
    } catch (error) {
      console.error('Error loading task data:', error);
    }
  };

  const [elapsedMs, setElapsedMs] = useState(0);
  
  const handleStop = () => {
    if (notes.trim().length === 0) {
      toast.error('Please add notes before stopping the timer');
      return;
    }
    
    const elapsed = stopTimer();
    setElapsedMs(elapsed);
    if (taskData) {
      setShowTimeEntryModal(true);
    }
  };

  const handleTimeEntrySaved = (completed: boolean) => {
    setShowTimeEntryModal(false);
    setNotes('');
    setTaskData(null);
    if (completed) {
      toast.success('Task completed!');
    } else {
      toast.success('Time entry saved');
    }
  };

  const handleBreakToggle = () => {
    if (isOnBreak) {
      endBreak();
    } else {
      startBreak();
    }
  };

  // Don't render if no timer is running
  if (!isRunning || !currentTaskId) {
    return null;
  }

  // Hide timer on auth pages
  const authPaths = ['/', '/login', '/register', '/reset-password'];
  if (authPaths.includes(location.pathname)) {
    return null;
  }

  return (
    <>
      {/* Floating Timer Bar */}
      <div className={`fixed bottom-0 left-0 right-0 z-50 transition-all duration-300 ${
        isMinimized ? 'transform translate-y-0' : 'transform -translate-y-0'
      }`}>
        <div className="bg-white border-t border-gray-200 shadow-lg">
          {/* Minimized View */}
          {isMinimized ? (
            <div className="px-4 py-2 flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <Clock className="h-5 w-5 text-gray-500" />
                <div>
                  <button
                    onClick={() => navigate('/focus', { state: { loadTaskId: currentTaskId } })}
                    className="font-medium text-sm text-gray-900 hover:text-indigo-600 text-left transition-colors"
                    title="Go to Focus Mode"
                  >
                    {currentTaskTitle}
                  </button>
                  <div className="font-mono text-lg font-bold text-green-600">{displayTime}</div>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                {isOnBreak ? (
                  <button
                    onClick={handleBreakToggle}
                    className="p-2 bg-amber-100 text-amber-700 rounded-md hover:bg-amber-200"
                    title="End break"
                  >
                    <Coffee className="h-5 w-5" />
                  </button>
                ) : (
                  <>
                    <button
                      onClick={togglePause}
                      className="p-2 bg-gray-100 rounded-md hover:bg-gray-200"
                      title={isRunning && !isOnBreak ? "Pause" : "Resume"}
                    >
                      {isRunning && !isOnBreak ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
                    </button>
                    <button
                      onClick={handleBreakToggle}
                      className="p-2 bg-gray-100 rounded-md hover:bg-gray-200"
                      title="Take a break"
                    >
                      <Coffee className="h-5 w-5" />
                    </button>
                  </>
                )}
                <button
                  onClick={() => navigate('/focus')}
                  className="p-2 bg-indigo-100 rounded-md hover:bg-indigo-200"
                  title="Focus Mode"
                >
                  <Zap className="h-5 w-5 text-indigo-600" />
                </button>
                <button
                  onClick={() => setIsMinimized(false)}
                  className="p-2 bg-gray-100 rounded-md hover:bg-gray-200"
                  title="Expand"
                >
                  <ChevronUp className="h-5 w-5" />
                </button>
              </div>
            </div>
          ) : (
            /* Expanded View */
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-medium text-lg text-gray-900">{currentTaskTitle}</h3>
                  <div className="font-mono text-2xl font-bold text-green-600">{displayTime}</div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => navigate('/focus')}
                    className="p-2 bg-indigo-100 rounded-md hover:bg-indigo-200"
                    title="Focus Mode"
                  >
                    <Zap className="h-5 w-5 text-indigo-600" />
                  </button>
                  <button
                    onClick={() => setIsMinimized(true)}
                    className="p-2 hover:bg-gray-100 rounded-md"
                    title="Minimize"
                  >
                    <Minimize2 className="h-5 w-5" />
                  </button>
                </div>
              </div>
              
              <div className="mb-3">
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add notes about your work (required to stop timer)..."
                  className="w-full p-3 border rounded-md resize-none"
                  rows={3}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  {isOnBreak ? (
                    <button
                      onClick={handleBreakToggle}
                      className="px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 flex items-center"
                    >
                      <Coffee className="h-5 w-5 mr-2" />
                      End Break
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={togglePause}
                        className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300 flex items-center"
                      >
                        {isRunning && !isOnBreak ? (
                          <>
                            <Pause className="h-5 w-5 mr-2" />
                            Pause
                          </>
                        ) : (
                          <>
                            <Play className="h-5 w-5 mr-2" />
                            Resume
                          </>
                        )}
                      </button>
                      <button
                        onClick={handleBreakToggle}
                        className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300 flex items-center"
                      >
                        <Coffee className="h-5 w-5 mr-2" />
                        Take Break
                      </button>
                    </>
                  )}
                </div>
                
                <button
                  onClick={handleStop}
                  disabled={notes.trim().length === 0}
                  className={`px-4 py-2 rounded-md flex items-center ${
                    notes.trim().length > 0
                      ? 'bg-red-600 text-white hover:bg-red-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  <Square className="h-5 w-5 mr-2" />
                  Stop Timer
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Break Overlay - Moved outside the timer container for proper centering */}
      {isOnBreak && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-75">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 shadow-2xl">
            <div className="text-center">
              <Coffee className="w-16 h-16 text-amber-600 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">On Break</h2>
              <p className="text-gray-600 mb-6">Timer is paused while you take a break</p>
              
              <button
                onClick={handleBreakToggle}
                className="px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 font-medium"
              >
                Resume Work
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Time Entry Modal */}
      {showTimeEntryModal && taskData && dbUserId && (
        <TimeEntryModal
          task={taskData}
          goal={null}
          matter={null}
          project={null}
          client={null}
          dbUserId={dbUserId}
          elapsedTime={displayTime}
          notes={notes}
          elapsedMs={elapsedMs}
          onClose={() => {
            setShowTimeEntryModal(false);
            setNotes('');
            setTaskData(null);
          }}
          onTimeEntrySaved={handleTimeEntrySaved}
        />
      )}
    </>
  );
}